import { Injectable } from '@angular/core';
import { PrintCardContentViewModel } from '../../cards/card-content/shared/print-card-content-view-model';
import { map, shareReplay } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { BarcodeUtils } from '../../../../../utils/barcode-utils';
import type { LabelData } from '../../../../../models/print-labels/label-data';
import type { PrintLabelMenu } from '../../../../../models/menu/print-label/print-label-menu';
import type { LabelStack } from '../../../../../models/menu/section/label-stacks/label-stack';
import { AssetUtils } from '../../../../../utils/asset-utils';
import { StrainTypeUtils } from '../../../../../utils/strain-type-utils';
import { StrainType } from '../../../../../models/enum/dto/strain-classification.enum';

@Injectable()
export abstract class PrintLabelContentViewModel extends PrintCardContentViewModel {
  public menu$: Observable<PrintLabelMenu>;
  public section$: Observable<LabelStack>;
  public labelStackMenu$ = this.menu$;
  public labelStack$ = this.section$;

  public labelData$ = this.rowViewModel$.pipe(
    map(labelData => (labelData as LabelData) ?? null),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly barCode$ = this.labelData$.pipe(map(labelData => labelData?.getBarCode() ?? null));

  public readonly barcodeToDisplay$ = this.labelData$.pipe(
    map(labelData => {
      // This will still render as a visual 'barcode' but will be overlaid with invalid state message
      const emptyBarcode = '00000000000000';
      const bc = labelData?.getBarCode();
      return BarcodeUtils.isValidGTIN14(bc) ? bc : emptyBarcode;
    })
  );

  public readonly barcodeIsValid$ = this.labelData$.pipe(
    map(labelData => BarcodeUtils.isValidGTIN14(labelData?.getBarCode()))
  );

  public readonly sku$ = this.labelData$.pipe(map(labelData => labelData?.getSKU() ?? null));

  public readonly labelAssetUrl$ = this.labelData$.pipe(
    map(labelData => AssetUtils.BaseInventoryLabelProductTypeUrls(labelData))
  );

  public readonly strainTypeColor$ = this.labelData$.pipe(
    map(labelData => this.getInventoryLabelStrainTypeColor(labelData?.getStrainType()))
  );

  public readonly strainTypeSymbol$ = this.labelData$.pipe(
    map(labelData => StrainTypeUtils.getSymbol(labelData?.getStrainType()))
  );

  protected getInventoryLabelStrainTypeColor(strainType: StrainType): string {
    switch (strainType) {
      case StrainType.Indica:
      case StrainType.IndicaDominant:
        return '#9E42FB';
      case StrainType.Sativa:
      case StrainType.SativaDominant:
        return '#DF7902';
      case StrainType.Hybrid:
        return '#05A76D';
      case StrainType.Blend:
        return '#057A9F';
      case StrainType.CBD:
        return '#AC1F49';
      default:
        return '#000000';
    }
  }
}
